import React from "react";
import PropTypes from "prop-types";
import { Box, Typography } from "@mui/material";
import { KeyboardArrowDown } from "@mui/icons-material";
import { alpha } from "@mui/material/styles";
import { motion } from "framer-motion";
import { colors } from "../../theme/colors";
import Loader from "../common/loader";

const MotionBox = motion(Box);

const GHOST_ROW_SX = {
    width: "100%",
    maxWidth: "100%",
    boxSizing: "border-box",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "0.3rem",
    minHeight: { xs: "2.35rem", md: "2.5rem" },
    px: "1.15rem",
    py: "0.35rem",
    borderRadius: "10px",
    position: "relative",
    overflow: "hidden",
    cursor: "pointer",
    userSelect: "none",
    border: "none",
    background: `
        linear-gradient(
            180deg,
            rgba(16,18,20,0.42) 0%,
            rgba(10,12,14,0.55) 100%
        )
    `,
    backdropFilter: "blur(10px)",
    boxShadow: "inset 0 1px 0 rgba(255,255,255,0.03)",
    transition:
        "background 180ms ease, box-shadow 180ms ease, color 180ms ease",
    color: "rgba(255,255,255,0.62)",
    "&:hover": {
        color: colors.text.primary,
        background: `
            linear-gradient(
                180deg,
                rgba(22,24,26,0.62) 0%,
                rgba(14,16,18,0.72) 100%
            )
        `,
        boxShadow: `
            inset 0 1px 0 rgba(255,255,255,0.05),
            0 8px 22px rgba(0,0,0,0.22)
        `,
    },
    "&:focus-visible": {
        outline: "none",
        boxShadow: `0 0 0 2px ${alpha(colors.primary.main, 0.22)}`,
    },
    "&::before": {
        content: '""',
        position: "absolute",
        top: 0,
        left: "50%",
        transform: "translateX(-50%)",
        width: "28%",
        height: "1px",
        background: `
            linear-gradient(
                90deg,
                transparent,
                rgba(246,185,76,0.35),
                transparent
            )
        `,
        opacity: 0.7,
        pointerEvents: "none",
    },
};

export default function ProfileTableShowMoreRow({
    onClick,
    isLoading = false,
    label = "Show more",
}) {
    if (isLoading) {
        return (
            <Box
                sx={{
                    ...GHOST_ROW_SX,
                    cursor: "default",
                    pointerEvents: "none",
                    "&:hover": {
                        color: "rgba(255,255,255,0.62)",
                        background: GHOST_ROW_SX.background,
                        boxShadow: GHOST_ROW_SX.boxShadow,
                    },
                }}
                aria-busy="true"
                aria-live="polite"
            >
                <Loader size={22} />
                <Typography
                    sx={{
                        fontSize: "0.78rem",
                        fontWeight: 650,
                        letterSpacing: "0.08em",
                        textTransform: "uppercase",
                        color: colors.text.secondary,
                    }}
                >
                    Loading
                </Typography>
            </Box>
        );
    }

    return (
        <MotionBox
            component="button"
            type="button"
            onClick={onClick}
            whileHover={{ y: -1 }}
            whileTap={{ y: 0 }}
            transition={{ duration: 0.15, ease: "easeInOut" }}
            sx={{
                ...GHOST_ROW_SX,
                m: 0,
                font: "inherit",
                appearance: "none",
                WebkitAppearance: "none",
            }}
            aria-label={label}
        >
            <Typography
                component="span"
                sx={{
                    fontSize: "0.75rem",
                    fontWeight: 700,
                    letterSpacing: "0.12em",
                    textTransform: "uppercase",
                    lineHeight: 1,
                    color: "inherit",
                }}
            >
                {label}
            </Typography>
            <KeyboardArrowDown
                sx={{
                    fontSize: "1.05rem",
                    color: "inherit",
                    opacity: 0.85,
                }}
            />
        </MotionBox>
    );
}

ProfileTableShowMoreRow.propTypes = {
    onClick: PropTypes.func,
    isLoading: PropTypes.bool,
    label: PropTypes.string,
};
